import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ClipboardList, Clock, Eye, FileText, Loader2,
  RotateCcw, Wallet,
} from 'lucide-react';
import { adminApi } from '../services/api';
import {
  annoncerErreur, annoncerSucces, Bandeau, demanderMotif, EtatErreur, SqueletteTableau,
} from '../etats';
import EtatVide from './EtatVide';
import { BoutonSigner } from './SignerDocument';

/* ═══════════════════════════════════════════════════════════════════════
   SUPERVISION — FICHES REÇUES, RELEVÉS VALIDÉS

   Ce que cet écran doit rendre évident, parce que c'est la raison d'être
   du dispositif : **une fiche non reçue n'est pas facturable.** Le point
   focal qui tarde à recevoir bloque la facturation de l'intervenant, et
   il ne le voit nulle part ailleurs.

   D'où l'ordre des onglets — ce qui attend une décision d'abord — et le
   compteur sur chaque onglet plutôt que dans un tableau de bord séparé.

   Le serveur répond **404 hors périmètre, pas 403** : confirmer
   l'existence d'un document dirait déjà quelque chose. Un 404 ici veut
   donc dire « ce n'est pas votre écran », pas « c'est cassé ».
   ═══════════════════════════════════════════════════════════════════════ */

const ETATS_FICHE = {
  transmise: { libelle: 'Transmise', couleur: 'var(--attention)', Icone: Clock },
  recue: { libelle: 'Reçue', couleur: 'var(--succes)', Icone: CheckCircle2 },
  a_corriger: { libelle: 'À corriger', couleur: 'var(--alerte)', Icone: RotateCcw },
  a_signer: { libelle: 'À signer', couleur: 'var(--text-muted)', Icone: FileText },
  brouillon: { libelle: 'Brouillon', couleur: 'var(--text-muted)', Icone: FileText },
};

const ETATS_RELEVE = {
  emis: { libelle: 'Émis', couleur: 'var(--text-muted)' },
  transmis: { libelle: 'Transmis', couleur: 'var(--attention)' },
  valide: { libelle: 'Validé', couleur: 'var(--succes)' },
  paye: { libelle: 'Payé', couleur: 'var(--succes)' },
  annule: { libelle: 'Annulé', couleur: 'var(--alerte)' },
};

function Etiquette({ etat, table }) {
  const { libelle, couleur } = table[etat] || { libelle: etat, couleur: 'var(--text-muted)' };
  return (
    <span className="text-[12px] font-semibold" style={{ color: couleur }}>
      {libelle}
    </span>
  );
}

function jour(valeur) {
  if (!valeur) return '—';
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
}

function francs(valeur) {
  const n = Number(valeur || 0);
  return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} FCFA`;
}

/* Une actualisation qui échoue garde ce qui est affiché et le dit. */
function AvisActualisation({ erreur, onReessayer }) {
  if (!erreur) return null;
  return (
    <Bandeau ton="attention" action={<button type="button" className="act act-sm act-contour" onClick={onReessayer}>Réessayer</button>}>
      L’actualisation a échoué : ces données sont peut-être anciennes.
    </Bandeau>
  );
}

/* ── Les fiches de suivi ──────────────────────────────────────────── */

function Fiches({ peutAgir, onCompteur, signaturePrete }) {
  const [fiches, setFiches] = useState(null);
  // L'objet erreur, pas son texte : un 404 ici veut dire « hors périmètre ».
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState('');

  const charger = useCallback(async () => {
    try {
      // `authFetch` rend ici les données parsées et lève de lui-même :
      // pas de `.ok` ni de `.json()` à attendre.
      const data = await adminApi.supervisionFiches();
      setFiches(data.fiches || []);
      setErreur(null);
      onCompteur?.((data.fiches || []).filter((f) => f.statut === 'transmise').length);
    } catch (e) {
      setErreur(e);
    }
  }, [onCompteur]);

  useEffect(() => { charger(); }, [charger]);

  const recevoir = async (fiche) => {
    setEnCours(fiche.id);
    try {
      await adminApi.recevoirFiche(fiche.id);
      annoncerSucces(`Fiche ${fiche.numero} reçue : elle devient facturable.`);
      await charger();
    } catch (e) {
      annoncerErreur(e.message || 'La fiche n’a pas pu être reçue.');
    } finally { setEnCours(''); }
  };

  const renvoyer = async (fiche) => {
    // Le motif est exigé côté serveur : le demander ici évite un
    // aller-retour pour se voir répondre « dites ce qui doit être corrigé ».
    const motif = await demanderMotif({
      titre: `Renvoyer la fiche ${fiche.numero} ?`,
      message: `Elle repart chez ${fiche.intervenant}. Dites ce qui doit être corrigé — il ne le devinera pas.`,
      libelle: 'Ce qui doit être corrigé', confirmer: 'Renvoyer',
    });
    if (!motif) return;
    setEnCours(fiche.id);
    try {
      await adminApi.renvoyerFiche(fiche.id, motif.trim());
      annoncerSucces(`Fiche ${fiche.numero} renvoyée à ${fiche.intervenant} pour correction.`);
      await charger();
    } catch (e) {
      annoncerErreur(e.message || 'La fiche n’a pas pu être renvoyée.');
    } finally { setEnCours(''); }
  };

  if (fiches === null) {
    return erreur
      ? <EtatErreur erreur={erreur} onReessayer={charger} />
      : <SqueletteTableau colonnes={7} />;
  }

  return (
    <div className="space-y-4">
      <AvisActualisation erreur={erreur} onReessayer={charger} />

      {fiches.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide
            icone={ClipboardList}
            titre="Aucune fiche de suivi"
            aide="Une fiche naît d'une séance réalisée, établie par l'intervenant qui l'a animée."
          />
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Référence</th>
                  <th scope="col">Intervenant</th>
                  <th scope="col">Organisation</th>
                  <th scope="col">Séance</th>
                  <th scope="col">État</th>
                  <th scope="col">Délai</th>
                  <th scope="col" className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {fiches.map((fiche) => (
                  <tr key={fiche.id}>
                    <th scope="row" className="font-normal">
                      <span className="text-[13.5px] font-medium text-[var(--text-bold)]">
                        {fiche.numero || '—'}
                      </span>
                      {fiche.version > 1 && (
                        <span className="block text-[11.5px] text-[var(--text-muted)]">
                          version {fiche.version}
                        </span>
                      )}
                    </th>
                    <td className="text-[13px]">{fiche.intervenant}</td>
                    <td className="text-[13px]">{fiche.organisation}</td>
                    <td className="text-[13px] text-[var(--text-muted)]">
                      {jour(fiche.date_seance)}
                    </td>
                    <td><Etiquette etat={fiche.statut} table={ETATS_FICHE} /></td>
                    <td>
                      {/* Le retard se juge sur la PREMIÈRE transmission :
                          une correction ne rattrape pas un retard, et ne
                          doit pas non plus en créer un. */}
                      {fiche.en_retard ? (
                        <span className="inline-flex items-center gap-1 text-[12px]"
                              style={{ color: 'var(--alerte)' }}>
                          <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                          Hors délai
                        </span>
                      ) : (
                        <span className="text-[12px] text-[var(--text-muted)]">Dans les temps</span>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <a href={adminApi.urlPdfFicheSupervision(fiche.id)}
                         target="_blank" rel="noopener noreferrer"
                         className="act act-tertiaire" title="Voir la fiche">
                        <Eye className="w-4 h-4" />
                      </a>
                      {/* Contreseing seulement derrière une signature
                          électronique : un document déposé en scan se
                          poursuit au scan. L'écran ne propose donc « Signer »
                          que si le titulaire a signé à l'écran. */}
                      {peutAgir && fiche.statut === 'transmise'
                        && fiche.signatures?.some((s) => s.bloc === 'intervenant')
                        && !fiche.signatures?.some((s) => s.bloc === 'reception') && (
                        <span className="inline-block ml-1">
                          <BoutonSigner
                            genre="fiches" document={fiche} bloc="reception"
                            libelleBloc="Votre contreseing, à la réception"
                            signatureEnregistree={signaturePrete}
                            onSigne={charger}
                            className="act act-tertiaire" />
                        </span>
                      )}
                      {peutAgir && fiche.statut === 'transmise' && (
                        <button type="button" onClick={() => recevoir(fiche)}
                                disabled={enCours === fiche.id} aria-busy={enCours === fiche.id || undefined}
                                className="act act-contour ml-1">
                          {enCours === fiche.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <CheckCircle2 className="w-4 h-4" />}
                          Recevoir
                        </button>
                      )}
                      {peutAgir && ['transmise', 'recue'].includes(fiche.statut) && (
                        <button type="button" onClick={() => renvoyer(fiche)}
                                disabled={enCours === fiche.id}
                                className="act act-tertiaire ml-1" title="Renvoyer pour correction">
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
        Une fiche <strong>non reçue n'est pas facturable</strong> : tant qu'elle
        attend ici, l'intervenant ne peut pas la porter sur son relevé du mois.
        Un renvoi efface les signatures — elles portaient sur la version
        renvoyée — et une fiche déjà facturée ne se renvoie plus : il faut
        renvoyer le relevé d'abord.
      </p>
    </div>
  );
}

/* ── Les relevés de prestation ────────────────────────────────────── */

/* Le détail d'un relevé : chaque séance facturée, son rapport et l'avis du
   bénéficiaire. Celui qui valide voit ce qu'il paie sans ouvrir dix PDF. */
function DetailReleve({ releve }) {
  return (
    <ul className="space-y-3 py-2">
      {(releve.lignes || []).map((l) => (
        <li key={l.fiche_id} className="rounded-pastille border border-[var(--border)] p-3 bg-[var(--bg-main)]">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <p className="text-[13px] font-semibold text-[var(--text-bold)]">
              {jour(l.date)} · {l.beneficiaire} <span className="font-normal text-[var(--text-muted)]">· {l.modalite} · {l.fiche}</span>
            </p>
            <p className="text-[13px] font-semibold tabular-nums">{francs(l.montant)}</p>
          </div>
          <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-[12.5px]">
            <div><dt className="text-[var(--text-muted)]">Objectifs</dt><dd className="whitespace-pre-line">{l.rapport.objectifs || '—'}</dd></div>
            <div><dt className="text-[var(--text-muted)]">Points traités</dt><dd className="whitespace-pre-line">{l.rapport.points_traites || '—'}</dd></div>
            <div><dt className="text-[var(--text-muted)]">Prochaines étapes</dt><dd className="whitespace-pre-line">{l.rapport.prochaines_etapes || '—'}</dd></div>
            <div><dt className="text-[var(--text-muted)]">Présence</dt><dd>{l.rapport.presence || '—'}{l.rapport.observations ? ` — ${l.rapport.observations}` : ''}</dd></div>
          </dl>
          <p className="text-[12px] mt-2 text-[var(--text-muted)]">
            Avis du bénéficiaire :{' '}
            {l.avis_beneficiaire
              ? <span className="text-[var(--text-bold)] font-semibold">{l.avis_beneficiaire.note}/10{l.avis_beneficiaire.commentaire ? ` — « ${l.avis_beneficiaire.commentaire} »` : ''}</span>
              : 'pas encore donné'}
          </p>
        </li>
      ))}
    </ul>
  );
}

function Releves({ peutAgir, onCompteur, signaturePrete }) {
  const [releves, setReleves] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState('');
  const [deplie, setDeplie] = useState(null);

  const charger = useCallback(async () => {
    try {
      const data = await adminApi.supervisionReleves();
      setReleves(data.releves || []);
      setErreur(null);
      onCompteur?.((data.releves || []).filter((r) => r.statut === 'transmis').length);
    } catch (e) {
      setErreur(e);
    }
  }, [onCompteur]);

  useEffect(() => { charger(); }, [charger]);

  const agir = async (releve, action) => {
    setEnCours(releve.id);
    try {
      if (action === 'valider') {
        await adminApi.validerReleve(releve.id);
        annoncerSucces(`Relevé ${releve.numero} validé.`);
      } else {
        const motif = await demanderMotif({
          titre: `Renvoyer le relevé ${releve.numero} ?`,
          message: 'Il repart chez l’intervenant pour correction.',
          libelle: 'Ce qui doit être corrigé', confirmer: 'Renvoyer',
        });
        if (!motif) { setEnCours(''); return; }
        await adminApi.renvoyerReleve(releve.id, motif.trim());
        annoncerSucces(`Relevé ${releve.numero} renvoyé pour correction.`);
      }
      await charger();
    } catch (e) {
      annoncerErreur(e.message || 'L’opération n’a pas abouti.');
    } finally { setEnCours(''); }
  };

  if (releves === null) {
    return erreur
      ? <EtatErreur erreur={erreur} onReessayer={charger} />
      : <SqueletteTableau colonnes={7} />;
  }

  return (
    <div className="space-y-4">
      <AvisActualisation erreur={erreur} onReessayer={charger} />

      {releves.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide
            icone={Wallet}
            titre="Aucun relevé de prestation"
            aide="Un relevé naît des fiches reçues du mois, émis par l'intervenant lui-même."
          />
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Référence</th>
                  <th scope="col">Prestataire</th>
                  <th scope="col">Mois</th>
                  <th scope="col">Prestations</th>
                  <th scope="col" className="text-right">Net à payer</th>
                  <th scope="col">État</th>
                  <th scope="col" className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {releves.map((releve) => (
                  <React.Fragment key={releve.id}>
                  <tr>
                    <th scope="row" className="font-normal">
                      <span className="text-[13.5px] font-medium text-[var(--text-bold)]">
                        {releve.numero}
                      </span>
                    </th>
                    <td className="text-[13px]">{releve.intervenant}</td>
                    <td className="text-[13px] text-[var(--text-muted)]">
                      {jour(releve.mois)}
                    </td>
                    <td className="text-[13px]">
                      <button type="button" className="act act-tertiaire"
                              aria-expanded={deplie === releve.id}
                              onClick={() => setDeplie(deplie === releve.id ? null : releve.id)}>
                        {releve.prestations} séance{releve.prestations > 1 ? 's' : ''} · {deplie === releve.id ? 'masquer' : 'voir les rapports'}
                      </button>
                    </td>
                    <td className="text-right text-[13.5px] font-semibold">
                      {francs(releve.montant_net)}
                    </td>
                    <td><Etiquette etat={releve.statut} table={ETATS_RELEVE} /></td>
                    <td className="text-right whitespace-nowrap">
                      <a href={adminApi.urlPdfReleveSupervision(releve.id)}
                         target="_blank" rel="noopener noreferrer"
                         className="act act-tertiaire" title="Voir le relevé">
                        <Eye className="w-4 h-4" />
                      </a>
                      {peutAgir && releve.statut === 'transmis'
                        && releve.signatures?.some((s) => s.bloc === 'prestataire')
                        && !releve.signatures?.some((s) => s.bloc === 'validation') && (
                        <span className="inline-block ml-1">
                          <BoutonSigner
                            genre="releves" document={releve} bloc="validation"
                            libelleBloc="Votre contreseing, à la validation"
                            signatureEnregistree={signaturePrete}
                            onSigne={charger}
                            className="act act-tertiaire" />
                        </span>
                      )}
                      {peutAgir && releve.statut === 'transmis' && (
                        <>
                          <button type="button" onClick={() => agir(releve, 'valider')}
                                  disabled={enCours === releve.id} aria-busy={enCours === releve.id || undefined}
                                  className="act act-contour ml-1">
                            {enCours === releve.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <CheckCircle2 className="w-4 h-4" />}
                            Valider
                          </button>
                          <button type="button" onClick={() => agir(releve, 'renvoyer')}
                                  disabled={enCours === releve.id}
                                  className="act act-tertiaire ml-1" title="Renvoyer">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {deplie === releve.id && (
                    <tr><td colSpan={7}><DetailReleve releve={releve} /></td></tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
        Valider, c'est attester que <strong>ces séances ont eu lieu et que ces
        montants sont conformes</strong>. Chaque ligne du relevé cite la fiche
        qui la justifie. Qui valide ne peut pas payer : le décaissement
        demande une autre personne.
      </p>
    </div>
  );
}

/* ── L'assemblage ─────────────────────────────────────────────────── */

export default function SupervisionDocuments({ user }) {
  const peutAgir = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);
  const [vue, setVue] = useState('fiches');
  // Proposer « Signer » à qui n'a pas enregistré sa signature, c'est
  // l'envoyer dans un refus.
  const [signaturePrete, setSignaturePrete] = useState(false);

  useEffect(() => {
    let vivant = true;
    adminApi.maSignature()
      .then((d) => { if (vivant) setSignaturePrete(!!d.enregistree); })
      .catch(() => { if (vivant) setSignaturePrete(false); });
    return () => { vivant = false; };
  }, []);
  const [attenteFiches, setAttenteFiches] = useState(null);
  const [attenteReleves, setAttenteReleves] = useState(null);

  const vues = [
    { cle: 'fiches', libelle: 'Fiches de suivi', attente: attenteFiches },
    { cle: 'releves', libelle: 'Relevés de prestation', attente: attenteReleves },
  ];

  return (
    <div className="space-y-5">
      <div className="flex gap-1 flex-wrap">
        {vues.map((v) => {
          const actif = vue === v.cle;
          return (
            <button
              key={v.cle}
              type="button"
              onClick={() => setVue(v.cle)}
              style={{ minHeight: 40,
                       color: actif ? 'var(--text-bold)' : 'var(--text-muted)',
                       boxShadow: actif ? 'inset 0 -2px 0 var(--accent)' : 'none' }}
              className="act act-tertiaire"
            >
              {v.libelle}
              {/* Le compteur porte sur ce qui ATTEND une décision. Un
                  total n'apprendrait rien : c'est le retard qui coûte. */}
              {v.attente > 0 && (
                <span className="ml-1.5 px-1.5 rounded-pastille text-[11px] font-semibold"
                      style={{ backgroundColor: 'var(--attention)', color: '#fff' }}>
                  {v.attente}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {vue === 'fiches'
        ? <Fiches peutAgir={peutAgir} onCompteur={setAttenteFiches}
                  signaturePrete={signaturePrete} />
        : <Releves peutAgir={peutAgir} onCompteur={setAttenteReleves}
                   signaturePrete={signaturePrete} />}
    </div>
  );
}
